import type { TerracePreference } from "@/types/table";
import type { CombinedTable, Reservation, Table, TableAvailability, TableBookablePeriod, TableWeekdayAvailability } from "@/types/table";
import type { ConflictCheckOptions } from "@/lib/reservationConflictUtils";
import { findOptimalTable } from "@/lib/tableAssignmentUtils";
import { formatDateLocal } from "@/lib/timeFormatUtils";
import { calculateEffectiveCapacity } from "@/lib/childrenCapacityUtils";
import { calculateMaxTotalForCapacity } from "@/lib/childrenCapacityUtils";
import { getBlockedTableIds } from "@/lib/reservationConflictUtils";
import { isTableAvailableOnDate, isTableBookableOnDate, isTableInTemporaryBlockingPeriod } from "@/lib/tableAvailabilityUtils";
import { generateAvailableTimes } from "@/lib/timeBookingUtils";
import { getSlotsForZonePreference, type ZoneWindow } from "@/lib/zoneScheduleUtils";
import type { TimeSlot, TimeSlotDateOverride } from "@/lib/timeSlotUtils";
import { durationForTime, filterBlockedTimes, slotLabelMinutes, type TimeBlockRow } from "../../supabase/functions/_shared/timeBlocks";

export type PublicAvailabilityReason = 'available' | 'full' | 'capacity' | 'hours' | 'closed' | 'incompatible';

export interface PlacementAvailability {
  status: PublicAvailabilityReason;
  label?: string;
}

export interface PublicBookingParty {
  total: number;
  adults: number;
  children: number;
  hasStroller: boolean;
}

export interface PublicSlotAvailability {
  time: string;
  /** TIME-grid alias kept alongside hasAvailability for both public display modes. */
  isAvailable: boolean;
  available: number;
  totalCapacity: number;
  availableCapacity: number;
  maxCapacity: number;
  singleCapacities: number[];
  singleMinCapacities: number[];
  combinedRawCapacities: number[];
  combinedEffectiveCapacities: number[];
  combinedMinCapacities: number[];
  hasAvailability: boolean;
  unavailableReason?: Exclude<PublicAvailabilityReason, 'available' | 'closed'> | 'none';
}

export function normalizePublicBookingParty(
  total: number,
  numberOfChildren: number,
  hasStroller: boolean,
  childrenEnabled: boolean,
  strollerEnabled: boolean,
): PublicBookingParty {
  const safeTotal = Number.isFinite(total) ? Math.max(0, Math.trunc(total)) : 0;
  const children = childrenEnabled
    ? Math.min(Math.max(0, Math.trunc(numberOfChildren)), Math.max(0, safeTotal - 1))
    : 0;
  return {
    total: safeTotal,
    adults: safeTotal - children,
    children,
    hasStroller: strollerEnabled && hasStroller,
  };
}

interface PublicSlotsForPlacementParams {
  date: Date;
  zonePreference: string[];
  bookingMode: 'slot' | 'time';
  regularSlots: TimeSlot[];
  dateOverrides: TimeSlotDateOverride[];
  zoneWindows: ZoneWindow[];
  activeZoneIds: Set<string>;
  isSlotClosedOnDate: (slotId: string, date: string) => boolean;
  timeBlocks: TimeBlockRow[];
  bookingIntervalMinutes: 15 | 30 | 60;
  reservationDurationMinutes: number;
}

/** Resolves exactly the starts shown publicly for one placement option. */
export function getPublicSlotsForPlacement({
  date, zonePreference, bookingMode, regularSlots, dateOverrides, zoneWindows,
  activeZoneIds, isSlotClosedOnDate, timeBlocks, bookingIntervalMinutes,
  reservationDurationMinutes,
}: PublicSlotsForPlacementParams): string[] {
  const dateStr = formatDateLocal(date);
  const slots = getSlotsForZonePreference(date, zonePreference, regularSlots, dateOverrides, zoneWindows, activeZoneIds);
  const hasOverrides = dateOverrides.some(item => item.date === dateStr && item.isActive);
  const openSlots = hasOverrides
    ? slots
    : slots.filter(slot => !slot.id || !isSlotClosedOnDate(slot.id, dateStr));
  if (bookingMode === 'slot') {
    const labels = openSlots.map(slot => `${slot.start} - ${slot.end}`);
    return filterBlockedTimes(dateStr, labels, timeBlocks, label => slotLabelMinutes(label));
  }
  const ranges = openSlots.map(slot => ({
    start: slot.start,
    end: slot.end,
    durationOverrideMinutes: slot.durationOverrideMinutes,
  }));
  return filterBlockedTimes(
    dateStr,
    generateAvailableTimes(ranges, bookingIntervalMinutes, reservationDurationMinutes),
    timeBlocks,
    time => durationForTime(time, ranges, reservationDurationMinutes),
  );
}

interface PublicSlotDiagnosisParams {
  date: Date;
  slot: string;
  party: PublicBookingParty;
  childrenCapacityMode: 'full' | 'half' | 'none';
  terracePreference: TerracePreference;
  zonePreference: string[];
  tables: Table[];
  combinedTables: CombinedTable[];
  reservations: Reservation[];
  availabilities: TableAvailability[];
  weekdayAvailabilities: TableWeekdayAvailability[];
  bookablePeriods: TableBookablePeriod[];
  conflictOptions?: ConflictCheckOptions;
  isTerraceClosed: (dateStr: string, slot: string) => boolean;
  customAssignmentOrderEnabled?: boolean;
}

interface PublicSlotsDiagnosisParams extends Omit<PublicSlotDiagnosisParams, 'slot' | 'conflictOptions'> {
  slots: string[];
  getConflictOptions: (slot: string) => ConflictCheckOptions | undefined;
  isHoursUnavailable?: (slot: string) => boolean;
}

/** Shared table snapshot used by SLOT and TIME grids in every public flow. */
export function diagnosePublicSlot({
  date, slot, party, childrenCapacityMode, terracePreference, zonePreference,
  tables, combinedTables, reservations, availabilities, weekdayAvailabilities,
  bookablePeriods, conflictOptions, isTerraceClosed, customAssignmentOrderEnabled = false,
}: PublicSlotDiagnosisParams): PublicSlotAvailability {
  const dateStr = formatDateLocal(date);
  const terraceClosed = isTerraceClosed(dateStr, slot);
  const blockedIds = getBlockedTableIds(reservations, combinedTables, date, slot, conflictOptions);
  const unavailableDates = availabilities.filter(item => !item.isAvailable);
  const hasChildren = party.children > 0;
  const zones = new Set(zonePreference);
  const matchesPlacement = (item: Table | CombinedTable) =>
    (zones.size === 0 || (item.zoneId != null && zones.has(item.zoneId)))
    && (terracePreference !== 'terrace' || item.isTerrace === true)
    && (terracePreference !== 'indoor' || item.isTerrace !== true)
    && !(terraceClosed && item.isTerrace === true);

  const single = tables.filter(table =>
    table.isActive && table.isBookable !== false
    && isTableBookableOnDate(table, date, bookablePeriods)
    && !blockedIds.has(table.id)
    && (!hasChildren || table.allowsChildren !== false)
    && (!party.hasStroller || table.allowsStroller !== false)
    && matchesPlacement(table)
    && isTableAvailableOnDate(
      table, date, unavailableDates,
      weekdayAvailabilities.find(item => item.tableId === table.id) ?? null,
    )
  );
  const combined = combinedTables.filter(item =>
    item.isActive && item.isBookable !== false
    && !blockedIds.has(item.id)
    && (!hasChildren || item.allowsChildren !== false)
    && (!party.hasStroller || item.allowsStroller !== false)
    && matchesPlacement(item)
    && item.tableIds.every(tableId => {
      const table = tables.find(candidate => candidate.id === tableId);
      return !!table && table.isActive !== false
        && !isTableInTemporaryBlockingPeriod(tableId, date, bookablePeriods)
        && (!hasChildren || table.allowsChildren !== false)
        && (!party.hasStroller || item.strollerPolicy === 'allow' || table.allowsStroller !== false)
        && isTableAvailableOnDate(
          table, date, unavailableDates,
          weekdayAvailabilities.find(entry => entry.tableId === tableId) ?? null,
        );
    })
  );
  const effectiveMax = (capacity: number) => hasChildren
    ? calculateMaxTotalForCapacity(capacity, party.children, childrenCapacityMode)
    : capacity;
  const singleCapacities = single.map(table => effectiveMax(table.capacity));
  const singleMinCapacities = single.map(table => table.minCapacity ?? 1);
  const combinedRawCapacities = combined.map(table => table.totalCapacity);
  const combinedEffectiveCapacities = combined.map(table => effectiveMax(table.totalCapacity));
  const combinedMinCapacities = combined.map(table => table.minCapacity ?? 1);
  const maxCapacity = Math.max(0, ...singleCapacities, ...combinedEffectiveCapacities);
  const assignment = party.total > 0 ? findOptimalTable(
    party.adults, date, slot, tables, combinedTables, reservations, availabilities,
    weekdayAvailabilities, conflictOptions, bookablePeriods, party.children,
    childrenCapacityMode, party.hasStroller, terracePreference,
    currentDate => isTerraceClosed(currentDate, slot), customAssignmentOrderEnabled,
    zonePreference,
  ) : null;

  const isAvailable = party.total > 0 ? assignment !== null : maxCapacity > 0;
  return {
    time: slot,
    isAvailable,
    available: single.length + combined.length,
    totalCapacity: tables.filter(table => table.isActive && table.isBookable !== false).reduce((sum, table) => sum + table.capacity, 0),
    availableCapacity: single.reduce((sum, table) => sum + table.capacity, 0),
    maxCapacity,
    singleCapacities,
    singleMinCapacities,
    combinedRawCapacities,
    combinedEffectiveCapacities,
    combinedMinCapacities,
    hasAvailability: isAvailable,
    unavailableReason: party.total > 0 && assignment === null
      ? (maxCapacity === 0 ? 'full' : 'capacity')
      : 'none',
  };
}

export function diagnosePublicSlots({
  slots, getConflictOptions, isHoursUnavailable, ...params
}: PublicSlotsDiagnosisParams): PublicSlotAvailability[] {
  return slots.map(slot => {
    const result = diagnosePublicSlot({ ...params, slot, conflictOptions: getConflictOptions(slot) });
    if (!isHoursUnavailable?.(slot)) return result;
    return { ...result, isAvailable: false, hasAvailability: false, unavailableReason: 'hours' as const };
  });
}

export function getPublicAvailabilityLabel(reason: PublicAvailabilityReason): string {
  switch (reason) {
    case 'available':
      return 'Disponible';
    case 'full':
      return 'Complet';
    case 'capacity':
      return 'Capacité insuffisante';
    case 'closed':
      return 'Fermé';
    case 'hours':
    case 'incompatible':
      return 'Indisponible';
  }
}

interface PlacementOption {
  key: string;
  terracePreference: TerracePreference;
  zonePreference: string[];
}

interface EvaluatePlacementOptionsParams {
  slots: string[];
  options: PlacementOption[];
  getSlotsForOption?: (option: PlacementOption) => string[];
  isAvailable: (slot: string, option: PlacementOption, ignoreReservations: boolean) => boolean;
  isClosed?: (slot: string, option: PlacementOption) => boolean;
  isCapacityInsufficient?: (option: PlacementOption) => boolean;
}

/**
 * Produces one stable, shared diagnosis for terrace and zone selectors.
 * The actual table assignment remains delegated to the existing assignment engine.
 */
export function evaluatePlacementOptions({
  slots,
  options,
  getSlotsForOption,
  isAvailable,
  isClosed,
  isCapacityInsufficient,
}: EvaluatePlacementOptionsParams): Record<string, PlacementAvailability> {
  return Object.fromEntries(options.map(option => {
    const optionSlots = getSlotsForOption?.(option) ?? slots;
    if (optionSlots.length === 0) {
      return [option.key, { status: 'incompatible' as const }];
    }

    if (optionSlots.some(slot => isAvailable(slot, option, false))) {
      return [option.key, { status: 'available' as const }];
    }

    if (isClosed && optionSlots.every(slot => isClosed(slot, option))) {
      return [option.key, { status: 'closed' as const }];
    }

    const structurallyPossible = optionSlots.some(slot => isAvailable(slot, option, true));
    if (structurallyPossible) return [option.key, { status: 'full' as const }];
    return [option.key, { status: isCapacityInsufficient?.(option) ? 'capacity' as const : 'incompatible' as const }];
  }));
}

/**
 * True when the customer's explicit placement choice (intérieur/terrasse or a
 * zone) is known to be unavailable for the current criteria. Public flows must
 * then KEEP the choice, clear the selected time and hide the time grid — they
 * must never silently fall back to another placement (Za'O incident, 2026-10).
 */
export function isSelectedPlacementUnavailable(
  terracePreference: TerracePreference,
  zonePreference: readonly string[],
  availability: Partial<Record<string, PlacementAvailability>>,
): boolean {
  const unavailable = (key: string) => {
    const status = availability[key]?.status;
    return status !== undefined && status !== 'available';
  };
  if (terracePreference !== 'no_preference' && unavailable(terracePreference)) return true;
  return zonePreference.some(unavailable);
}

export function getPlacementHelpMessage(
  subject: string,
  availability?: PlacementAvailability,
): string | undefined {
  if (!availability || availability.status === 'available') return undefined;
  if (availability.status === 'full') {
    return `Il n’y a plus de place disponible ${subject} pour ces critères. Choisissez une autre option ou une autre date.`;
  }
  if (availability.status === 'closed') {
    return `${subject.charAt(0).toUpperCase()}${subject.slice(1)} est fermée pour cette date. Choisissez une autre option.`;
  }
  return `Il n’est pas possible de réserver ${subject} avec ces critères. Choisissez une autre option.`;
}

interface BookingPlacementDiagnosisParams {
  date: Date;
  options: PlacementOption[];
  getSlots: (option: PlacementOption) => string[];
  adults: number;
  numberOfChildren: number;
  childrenCapacityMode: 'full' | 'half' | 'none';
  hasStroller: boolean;
  tables: Table[];
  combinedTables: CombinedTable[];
  reservations: Reservation[];
  availabilities: TableAvailability[];
  weekdayAvailabilities: TableWeekdayAvailability[];
  bookablePeriods: TableBookablePeriod[];
  getConflictOptions: (slot: string) => ConflictCheckOptions | undefined;
  isTerraceClosed: (dateStr: string, slot: string) => boolean;
  customAssignmentOrderEnabled?: boolean;
}

export function diagnoseBookingPlacement({
  date, options, getSlots, adults, numberOfChildren, childrenCapacityMode, hasStroller,
  tables, combinedTables, reservations, availabilities, weekdayAvailabilities,
  bookablePeriods, getConflictOptions, isTerraceClosed, customAssignmentOrderEnabled = false,
}: BookingPlacementDiagnosisParams): Record<string, PlacementAvailability> {
  const dateStr = formatDateLocal(date);
  const slotsByOption = new Map(options.map(option => [option.key, getSlots(option)]));
  const allSlots = [...new Set([...slotsByOption.values()].flat())];

  return evaluatePlacementOptions({
    slots: allSlots,
    options,
    getSlotsForOption: option => slotsByOption.get(option.key) ?? [],
    isAvailable: (slot, option, ignoreReservations) => {
      if (!slotsByOption.get(option.key)?.includes(slot)) return false;
      return findOptimalTable(
        adults, date, slot, tables, combinedTables,
        ignoreReservations ? [] : reservations,
        availabilities, weekdayAvailabilities, getConflictOptions(slot), bookablePeriods,
        numberOfChildren, childrenCapacityMode, hasStroller, option.terracePreference,
        (dateStr) => isTerraceClosed(dateStr, slot), customAssignmentOrderEnabled,
        option.zonePreference,
      ) !== null;
    },
    isClosed: (slot, option) => {
      const zoneIds = new Set(option.zonePreference);
      const matchingTables = tables.filter(table =>
        table.isActive && table.isBookable
        && (zoneIds.size === 0 || (table.zoneId != null && zoneIds.has(table.zoneId)))
        && (option.terracePreference !== 'terrace' || table.isTerrace === true)
        && (option.terracePreference !== 'indoor' || table.isTerrace !== true)
      );
      const isTerraceOnly = matchingTables.length > 0 && matchingTables.every(table => table.isTerrace === true);
      return (option.terracePreference === 'terrace' || isTerraceOnly) && isTerraceClosed(dateStr, slot);
    },
    isCapacityInsufficient: option => {
      const effectiveCapacity = calculateEffectiveCapacity(adults, numberOfChildren, childrenCapacityMode);
      const zoneIds = new Set(option.zonePreference);
      const matches = (item: Table | CombinedTable) =>
        item.isActive && item.isBookable
        && (numberOfChildren === 0 || item.allowsChildren !== false)
        && (!hasStroller || item.allowsStroller !== false)
        && (zoneIds.size === 0 || (item.zoneId != null && zoneIds.has(item.zoneId)))
        && (option.terracePreference !== 'terrace' || item.isTerrace === true)
        && (option.terracePreference !== 'indoor' || item.isTerrace !== true);
      const capacities = [
        ...tables.filter(matches).map(table => table.capacity),
        ...combinedTables.filter(matches).map(table => table.totalCapacity),
      ];
      return capacities.length > 0 && effectiveCapacity > Math.max(...capacities);
    },
  });
}
/**
 * Terrasse désactivée = terrasse fermée en ligne : le public n'est jamais placé
 * sur une table terrasse (seule l'équipe peut l'attribuer). Doit rester aligné
 * avec create-reservation, check-availability et update-reservation-public.
 */
export function resolvePublicTerracePreference(
  terraceEnabled: boolean | undefined | null,
  preference: TerracePreference,
): TerracePreference {
  return terraceEnabled === true ? preference : 'indoor';
}
